import { FC, useMemo, useState } from 'react';
import {
  Grid,
  Typography,
  Box,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import ControlledSelectField from '../../../../../components/controlledComponents/ControlledSelectField.component';
import ControlledDateField from '../../../../../components/controlledComponents/ControlledDateField.component';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { useStudentDetailsAssessmentsHook } from '../../../../../../apis/students/studentDetails/useStudentDetailsService';
import PaginatedGrid from '../../../../../components/paginatedGrid/PaginatedGrid';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';

interface StudentAssessmentsTabProps {
  studentId: string;
}

const StudentAssessmentsTab: FC<StudentAssessmentsTabProps> = ({ studentId }) => {
  const { translateText } = useTranslation();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [subjectFilter, setSubjectFilter] = useState<string>('all');
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');

  const { data, isLoading, error } = useStudentDetailsAssessmentsHook(
    studentId,
    {
      page,
      limit: pageSize,
      ...(subjectFilter !== 'all' && { subject: subjectFilter }),
      ...(fromDate && { fromDate }),
      ...(toDate && { toDate }),
    }
  );

  const assessments = useMemo(() => data?.data || [], [data]);
  const totalCount = useMemo(() => data?.total ?? 0, [data]);

  const subjects = useMemo(() => {
    const uniqueSubjects = Array.from(new Set(assessments.map((a) => a.subject)));
    return uniqueSubjects.sort();
  }, [assessments]);

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'subject',
        headerName: translateText('students.studentDetails.assessments.columns.subject'),
        width: 150,
        flex: 1,
      },
      {
        field: 'title',
        headerName: translateText('students.studentDetails.assessments.columns.title'),
        width: 200,
        flex: 1,
      },
      {
        field: 'score',
        headerName: translateText('students.studentDetails.assessments.columns.score'),
        width: 100,
        type: 'number',
      },
      {
        field: 'maxScore',
        headerName: translateText('students.studentDetails.assessments.columns.maxScore'),
        width: 100,
        type: 'number',
      },
      {
        field: 'percentage',
        headerName: translateText('students.studentDetails.assessments.columns.percentage'),
        width: 120,
        renderCell: (params: GridRenderCellParams) => {
          const assessment = params.row;
          const percentage = (assessment.score / assessment.maxScore) * 100;
          return `${percentage.toFixed(1)}%`;
        },
      },
      {
        field: 'grade',
        headerName: translateText('students.studentDetails.assessments.columns.grade'),
        width: 100,
        renderCell: (params: GridRenderCellParams) => {
          const grade = params.value as string;
          const colorMap: Record<string, 'success' | 'warning' | 'error' | 'info'> = {
            'A+': 'success',
            'A': 'success',
            'B+': 'info',
            'B': 'info',
            'C+': 'warning',
            'C': 'warning',
            'D': 'error',
            'F': 'error',
          };
          return (
            <Chip
              label={grade}
              size="small"
              color={colorMap[grade] || 'default'}
              variant="outlined"
            />
          );
        },
      },
      {
        field: 'date',
        headerName: translateText('students.studentDetails.assessments.columns.date'),
        width: 150,
        renderCell: (params: GridRenderCellParams) => {
          const date = new Date(params.value);
          return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          });
        },
      },
    ],
    [translateText]
  );

  const rows = useMemo(() => {
    return assessments.map((assessment) => ({
      id: assessment.id,
      subject: assessment.subject,
      title: assessment.title,
      score: assessment.score,
      maxScore: assessment.maxScore,
      grade: assessment.grade,
      date: assessment.date,
    }));
  }, [assessments]);

  const handlePageChange = (newPage: number, newPageSize: number) => {
    setPage(newPage);
    setPageSize(newPageSize);
  };

  if (!studentId) {
    return (
      <Grid
        container
        spacing={{ xs: 1, sm: 2 }}
        sx={{ p: { xs: 1, sm: 2 } }}
        data-testid="student-assessments-tab-container"
      >
        <Grid item xs={12}>
          <Alert severity="info" data-testid="student-assessments-tab-no-student">
            {translateText('students.studentDetails.assessments.noStudentId')}
          </Alert>
        </Grid>
      </Grid>
    );
  }

  if (isLoading) {
    return (
      <Grid
        container
        spacing={{ xs: 1, sm: 2 }}
        sx={{ p: { xs: 1, sm: 2 } }}
        data-testid="student-assessments-tab-container"
      >
        <Grid item xs={12}>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="60vh"
            data-testid="student-assessments-tab-loading"
          >
            <CircularProgress />
            <Typography
              variant="body2"
              sx={{ ml: 2, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
            >
              {translateText('common.loading')}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    );
  }

  if (error) {
    return (
      <Grid
        container
        spacing={{ xs: 1, sm: 2 }}
        sx={{ p: { xs: 1, sm: 2 } }}
        data-testid="student-assessments-tab-container"
      >
        <Grid item xs={12}>
          <Alert severity="error" data-testid="student-assessments-tab-error">
            {translateText('common.error')}
          </Alert>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid
      container
      spacing={{ xs: 1, sm: 2 }}
      sx={{ p: { xs: 1, sm: 2 } }}
      data-testid="student-assessments-tab-container"
    >
      <Grid item xs={12}>
        <Box
          display="flex"
          flexDirection={{ xs: 'column', sm: 'row' }}
          gap={2}
          mb={2}
          data-testid="student-assessments-tab-filters"
        >
          <Box sx={{ minWidth: { xs: '100%', sm: 150 } }}>
            <ControlledSelectField
              label={translateText('students.studentDetails.assessments.filters.subject')}
              id="subject-filter"
              name="subjectFilter"
              value={subjectFilter}
              onChangeHandler={(e) => setSubjectFilter(e.target.value)}
              itemList={[
                { key: 'all', value: translateText('common.all') },
                ...subjects.map((subject) => ({ key: subject, value: subject })),
              ]}
              isGridItem={false}
              dataTestId="student-assessments-tab-subject-filter"
            />
          </Box>
          <Box sx={{ minWidth: { xs: '100%', sm: 150 } }}>
            <ControlledDateField
              label={translateText('students.studentDetails.assessments.filters.fromDate')}
              id="from-date-filter"
              name="fromDate"
              value={fromDate ? dayjs(fromDate) : null}
              onChange={(date: Dayjs | null) => setFromDate(date ? date.format('YYYY-MM-DD') : '')}
              isGridItem={false}
            />
          </Box>
          <Box sx={{ minWidth: { xs: '100%', sm: 150 } }}>
            <ControlledDateField
              label={translateText('students.studentDetails.assessments.filters.toDate')}
              id="to-date-filter"
              name="toDate"
              value={toDate ? dayjs(toDate) : null}
              onChange={(date: Dayjs | null) => setToDate(date ? date.format('YYYY-MM-DD') : '')}
              isGridItem={false}
            />
          </Box>
        </Box>
      </Grid>
      <Grid item xs={12}>
        <PaginatedGrid
          rows={rows}
          columns={columns}
          loading={isLoading}
          dataTestId="student-assessments-tab-grid"
          serverSide={true}
          totalCount={totalCount}
          currentPage={page}
          currentPageSize={pageSize}
          onPageChange={handlePageChange}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Grid>
    </Grid>
  );
};

export default StudentAssessmentsTab;
